// Where a spelling occurs in a Narration Script, and what one edit changed.
//
// Two callers need the same notion of "this word": the render path, which
// swaps every Style Sheet spelling for its pronunciation, and Review, which
// offers to carry a word the Author just changed to every other place the Book
// uses it. They share one matcher so that "the places this term is spoken" and
// "the places this edit is offered" are never two different sets.
//
// Word boundaries are Unicode, never `\b` or `\w`: those are ASCII in
// JavaScript, so `\bStraß\b` matches inside "Straße". The class below is
// letters, numbers, the underscore, and combining marks; a mark belongs to the
// letter in front of it, and a match may not end between the two.
//
// Offsets are UTF-16 code units, as in `AppliedTerm.scriptOffset`: they index
// the Block's text directly.

/** A literal as a regex source. `-` stays unescaped: `\-` is invalid under the `u` flag. */
const escapeForRegExp = (literal: string): string =>
  literal.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&");

const WORD_CHAR = "\\p{L}\\p{N}_\\p{M}";

/** A whole-word, case-sensitive match on `spelling`, with Unicode word boundaries. */
export const wholeWord = (spelling: string): RegExp =>
  new RegExp(`(?<![${WORD_CHAR}])${escapeForRegExp(spelling)}(?![${WORD_CHAR}])`, "gu");

/** Every offset where `spelling` stands as a whole word in `text`, in order. */
export function findWholeWord(text: string, spelling: string): number[] {
  if (spelling.trim() === "") return [];
  return [...text.matchAll(wholeWord(spelling))].map((match) => match.index);
}

/** One word (or short run of words) the Author replaced with another. */
export interface WordChange {
  readonly from: string;
  readonly to: string;
}

/**
 * The longest `from` that still counts as one word change. A name with a
 * title and two given names fits; a rewritten clause does not, and offering
 * to rewrite that clause everywhere else would be a guess, not a suggestion.
 */
export const WORD_CHANGE_MAX_WORDS = 5;
export const WORD_CHANGE_MAX_LENGTH = 80;

/** Words, single non-word characters, and whitespace runs; joined, they are the text again. */
const tokenize = (text: string): string[] =>
  text.match(new RegExp(`[${WORD_CHAR}]+|\\s+|[^${WORD_CHAR}\\s]`, "gu")) ?? [];

const isWordToken = (token: string): boolean => new RegExp(`^[${WORD_CHAR}]`, "u").test(token);
const isSpace = (token: string): boolean => token.trim() === "";

interface Hunk {
  from: string;
  to: string;
}

type Step = { readonly kind: "same" | "from" | "to"; readonly token: string };

/**
 * Token-level longest common subsequence, grouped into hunks. A whitespace
 * token both sides keep does not end a hunk when more changes follow it, so
 * "Anna Schmitt" to "Hanna Schmidt" is one hunk and not two.
 */
function hunksOf(allBefore: string[], allAfter: string[]): Hunk[] {
  // The tokens both ends share never belong to a hunk; only the middle needs the table.
  let head = 0;
  while (head < allBefore.length && head < allAfter.length && allBefore[head] === allAfter[head])
    head++;
  let tail = 0;
  while (
    tail < allBefore.length - head &&
    tail < allAfter.length - head &&
    allBefore[allBefore.length - 1 - tail] === allAfter[allAfter.length - 1 - tail]
  )
    tail++;
  const before = allBefore.slice(head, allBefore.length - tail);
  const after = allAfter.slice(head, allAfter.length - tail);
  const n = before.length;
  const m = after.length;
  // lcs[i][j]: length of the common subsequence of before[i..] and after[j..].
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    const row = lcs[i] as number[];
    const below = lcs[i + 1] as number[];
    for (let j = m - 1; j >= 0; j--) {
      row[j] =
        before[i] === after[j]
          ? (below[j + 1] as number) + 1
          : Math.max(below[j] as number, row[j + 1] as number);
    }
  }

  const steps: Step[] = [];
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && before[i] === after[j]) {
      steps.push({ kind: "same", token: before[i] as string });
      i++;
      j++;
    } else if (j < m && (i >= n || (lcs[i]?.[j + 1] as number) >= (lcs[i + 1]?.[j] as number))) {
      steps.push({ kind: "to", token: after[j] as string });
      j++;
    } else {
      steps.push({ kind: "from", token: before[i] as string });
      i++;
    }
  }

  // The whole text as steps again, so a hunk can see the tokens around it.
  const all: Step[] = [
    ...allBefore.slice(0, head).map((token) => ({ kind: "same" as const, token })),
    ...steps,
    ...allBefore.slice(allBefore.length - tail).map((token) => ({ kind: "same" as const, token })),
  ];
  const hunks: Hunk[] = [];
  let open: { hunk: Hunk; start: number; end: number } | null = null;
  let gap = "";
  const close = () => {
    if (open !== null) hunks.push(attached(all, open.hunk, open.start, open.end));
    open = null;
    gap = "";
  };
  all.forEach((step, index) => {
    if (step.kind === "same") {
      if (open !== null && isSpace(step.token)) {
        gap += step.token;
        return;
      }
      close();
      return;
    }
    if (open === null) open = { hunk: { from: "", to: "" }, start: index, end: index };
    else if (gap !== "") {
      open.hunk.from += gap;
      open.hunk.to += gap;
    }
    gap = "";
    open.end = index;
    if (step.kind === "from") open.hunk.from += step.token;
    else open.hunk.to += step.token;
  });
  close();
  return hunks;
}

/**
 * A pure insertion or deletion that touches a word with no space between
 * takes that word into both sides: "Meier" to "Meier-Schulz" is a change of
 * the name, not an insertion of "-Schulz". Only text that carries a word of
 * its own attaches, so adding a period or a comma stays punctuation. A hunk
 * that starts or ends with a space is left alone, so adding a word to a
 * sentence stays an insertion, and only word characters are taken in, so an
 * opening quotation mark never becomes part of the name.
 */
function attached(steps: readonly Step[], hunk: Hunk, start: number, end: number): Hunk {
  if (hunk.from !== "" && hunk.to !== "") return hunk;
  const text = hunk.from || hunk.to;
  if (wordCount(text) === 0) return hunk;
  let before = "";
  let after = "";
  if (!isSpace(text.slice(0, 1)))
    for (let i = start - 1; i >= 0; i--) {
      const step = steps[i] as Step;
      if (step.kind !== "same" || isSpace(step.token) || !isWordToken(step.token)) break;
      before = step.token + before;
    }
  if (!isSpace(text.slice(-1)))
    for (let i = end + 1; i < steps.length; i++) {
      const step = steps[i] as Step;
      if (step.kind !== "same" || isSpace(step.token) || !isWordToken(step.token)) break;
      after += step.token;
    }
  if (before === "" && after === "") return hunk;
  return { from: before + hunk.from + after, to: before + hunk.to + after };
}

const wordCount = (text: string): number => tokenize(text).filter(isWordToken).length;

/**
 * The one word change an edit made, or null when it made something else.
 *
 * Null for an insertion or a deletion, for two different changes in the same
 * edit, for a change longer than a short name, and for a change of
 * punctuation or spacing alone. Replacing the same word the same way in
 * several places of one Block still counts as one change.
 */
export function detectWordChange(before: string, after: string): WordChange | null {
  if (before === after) return null;
  const hunks = hunksOf(tokenize(before), tokenize(after)).map((hunk) => ({
    from: hunk.from.trim(),
    to: hunk.to.trim(),
  }));
  const first = hunks[0];
  if (first === undefined) return null;
  if (!hunks.every((hunk) => hunk.from === first.from && hunk.to === first.to)) return null;
  if (first.from === "" || first.to === "") return null;
  if (wordCount(first.from) === 0 || wordCount(first.to) === 0) return null;
  if (wordCount(first.from) > WORD_CHANGE_MAX_WORDS) return null;
  if (first.from.length > WORD_CHANGE_MAX_LENGTH) return null;
  return first;
}

/**
 * The longest ending a form may add to the changed word: enough for a German
 * genitive or plural ("Meiers", "Meiern") and an English "-ed", short enough
 * that most compounds ("Meierhaus") are never candidates. The short ones that
 * remain ("Meierhof") are left to the judgment that follows.
 */
export const FORM_SUFFIX_MAX_LETTERS = 3;

/** One place a word may need the change: the word itself, or it with an ending. */
export interface Occurrence {
  readonly offset: number;
  /** What stands in the text: `from` itself, or `from` plus an ending. */
  readonly found: string;
}

const endsInLetter = (text: string): boolean => /\p{L}$/u.test(text);

/**
 * Places where `from` stands with a short ending of letters: the candidates
 * for an inflected form. Code only finds them; whether "Meiers" is the name
 * with a genitive and "Meierei" a different word is a judgment for the caller.
 * A `from` that ends in anything but a letter ("Dr.") takes no endings.
 */
export function findFormCandidates(text: string, from: string): Occurrence[] {
  if (from.trim() === "" || !endsInLetter(from)) return [];
  const pattern = new RegExp(
    `(?<![${WORD_CHAR}])${escapeForRegExp(from)}\\p{L}{1,${FORM_SUFFIX_MAX_LETTERS}}(?![${WORD_CHAR}])`,
    "gu",
  );
  return [...text.matchAll(pattern)].map((match) => ({ offset: match.index, found: match[0] }));
}

/**
 * What `found` becomes under the change: `to` for the word itself, `to` with
 * the same ending for a form ("Meiers" to "Maiers"), null for anything else.
 * The ending is carried over by code; whether the result is a real word is a
 * judgment the caller makes before offering it.
 */
export function replacementFor(change: WordChange, found: string): string | null {
  if (found === change.from) return change.to;
  if (!found.startsWith(change.from) || !endsInLetter(change.from)) return null;
  const ending = found.slice(change.from.length);
  if (!new RegExp(`^\\p{L}{1,${FORM_SUFFIX_MAX_LETTERS}}$`, "u").test(ending)) return null;
  return change.to + ending;
}

/**
 * Whether the span sits inside a place where the text already has the new
 * spelling. Changing "Meier" to "Meier-Schulz" must never offer the "Meier"
 * of "Meier-Schulz" again, or accepting it twice would stack the change.
 */
export function insideNewSpelling(
  text: string,
  change: WordChange,
  occurrence: Occurrence,
): boolean {
  if (!change.to.includes(change.from)) return false;
  const end = occurrence.offset + occurrence.found.length;
  return findWholeWord(text, change.to).some(
    (start) => start < end && occurrence.offset < start + change.to.length,
  );
}

/** Every place `text` uses the changed word itself, outside the new spelling. */
export function exactOccurrences(text: string, change: WordChange): Occurrence[] {
  return findWholeWord(text, change.from)
    .map((offset) => ({ offset, found: change.from }))
    .filter((occurrence) => !insideNewSpelling(text, change, occurrence));
}

/**
 * Whether the change only adds or drops a short ending ("Schmitt" to
 * "Schmitts"): a grammatical correction for one sentence, which is rarely
 * right for every other place the word stands.
 */
export function isEndingChange(change: WordChange): boolean {
  const [short, long] =
    change.from.length < change.to.length ? [change.from, change.to] : [change.to, change.from];
  return (
    long.startsWith(short) &&
    new RegExp(`^\\p{L}{1,${FORM_SUFFIX_MAX_LETTERS}}$`, "u").test(long.slice(short.length))
  );
}

/**
 * `text` with the change made at each occurrence that still holds what it
 * found, as a whole word. An occurrence that no longer does is skipped rather
 * than guessed at, so a suggestion computed against an older text never
 * rewrites the wrong words. Returns the new text and how many were applied.
 */
export function applyChange(
  text: string,
  change: WordChange,
  occurrences: readonly Occurrence[],
): { text: string; applied: number } {
  const seen = new Set<number>();
  const valid = occurrences
    .filter((occurrence) => {
      if (seen.has(occurrence.offset)) return false;
      seen.add(occurrence.offset);
      return (
        findWholeWord(text, occurrence.found).includes(occurrence.offset) &&
        !insideNewSpelling(text, change, occurrence)
      );
    })
    .map((occurrence) => ({ ...occurrence, to: replacementFor(change, occurrence.found) }))
    .filter((occurrence): occurrence is Occurrence & { to: string } => occurrence.to !== null)
    .sort((a, b) => b.offset - a.offset);
  let result = text;
  let last = Number.POSITIVE_INFINITY;
  let applied = 0;
  // Right to left, so a replacement never moves an offset still to come.
  for (const occurrence of valid) {
    const end = occurrence.offset + occurrence.found.length;
    if (end > last) continue;
    result = result.slice(0, occurrence.offset) + occurrence.to + result.slice(end);
    last = occurrence.offset;
    applied++;
  }
  return { text: result, applied };
}

/** Context around one occurrence, cut at word boundaries, for a suggestion card. */
export interface Snippet {
  readonly before: string;
  readonly match: string;
  readonly after: string;
  /** Whether text was cut off in front of `before` or behind `after`. */
  readonly clippedStart: boolean;
  readonly clippedEnd: boolean;
}

export const SNIPPET_RADIUS = 60;

export function snippetAround(
  text: string,
  offset: number,
  length: number,
  radius: number = SNIPPET_RADIUS,
): Snippet {
  let start = Math.max(0, offset - radius);
  let end = Math.min(text.length, offset + length + radius);
  // Widen to the nearest space so the snippet never starts or ends mid-word.
  if (start > 0) {
    const space = text.lastIndexOf(" ", start);
    start = space === -1 ? 0 : space + 1;
  }
  if (end < text.length) {
    const space = text.indexOf(" ", end);
    end = space === -1 ? text.length : space;
  }
  return {
    before: text.slice(start, offset),
    match: text.slice(offset, offset + length),
    after: text.slice(offset + length, end),
    clippedStart: start > 0,
    clippedEnd: end < text.length,
  };
}
